import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useState, useCallback } from 'react'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import { CreatorCollectorCard } from '@/components/auth/AccountTypeCard'
import { useOnboardingStore, AccountType, waitForOnboardingHydration } from '@/stores/onboarding'
import {
    useRegisterUser,
    useGoogleCompleteRegistration,
    useAppleCompleteRegistration,
    useLogin,
    useSendEmailVerification,
} from '@/hooks/api/useAuth'
import { Role } from '@wtf/shared'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { usePostHog } from 'posthog-js/react'
import { AnalyticsEvents } from '@/utils/analytics'
import { getIdToken } from '@/auth/firebase'
import { captureException } from '@/config/sentry'

export const Route = createFileRoute(
    '/(onboarding)/(auth)/register/account-type'
)({
    validateSearch: (search) => ({
        returnToPath:
            typeof search.returnToPath === 'string'
                ? search.returnToPath
                : undefined,
        error:
            typeof search.error === 'string'
                ? search.error
                : undefined,
    }),
    beforeLoad: async () => {
        // Wait for store hydration from sessionStorage before checking state
        await waitForOnboardingHydration()
        const { registrationData } = useOnboardingStore.getState()
        if (!registrationData.preference) {
            throw redirect({ to: '/register/preferences', search: { returnToPath: undefined, error: 'missing-preference' } })
        }
    },
    component: RegisterAccountTypeRoute,
})

function RegisterAccountTypeRoute() {
    const {
        registrationData,
        setRegistrationData,
        markStepCompleted,
        resetRegistrationData,
    } = useOnboardingStore()
    const navigate = useNavigate()
    const { returnToPath } = Route.useSearch()
    const [apiError, setApiError] = useState<string | null>(null)
    const [option, setOption] = useState<AccountType>('creator')
    const posthog = usePostHog()

    const { mutate: registerUser, isPending: isRegisteringUser } =
        useRegisterUser()
    const {
        mutate: googleCompleteRegistration,
        isPending: isRegisteringWithGoogle,
    } = useGoogleCompleteRegistration()
    const {
        mutate: appleCompleteRegistration,
        isPending: isRegisteringWithApple,
    } = useAppleCompleteRegistration()
    const { mutate: loginUser, isPending: isLoggingIn } = useLogin()
    const { mutate: sendVerification } = useSendEmailVerification()

    const isLoading =
        isRegisteringUser || isRegisteringWithGoogle || isRegisteringWithApple || isLoggingIn
    const isSocialSignIn = registrationData.authMethod === 'google' || registrationData.authMethod === 'apple'

    const getErrorMessage = (error: Error | null): string | null => {
        if (!error) return null
        const msg = error.message?.toLowerCase() || ''
        if (msg.includes('username already exists')) {
            return 'Username conflict during final registration. Please go back.'
        }
        // More flexible email conflict detection
        if (
            (msg.includes('email') &&
                (msg.includes('exist') ||
                    msg.includes('already') ||
                    msg.includes('registered') ||
                    msg.includes('taken'))) ||
            (msg.includes('user') &&
                msg.includes('email') &&
                (msg.includes('exist') || msg.includes('already'))) ||
            msg.includes('email already') ||
            msg.includes('already registered') ||
            msg.includes('account with this email')
        ) {
            return 'An account with this email address already exists. Please log in or use a different email.'
        }
        if (msg.includes('invalid token')) {
            return 'Authentication session expired. Please sign in again.'
        }
        return 'Registration failed. Please check your details and try again.'
    }

    // Memoized success handler (defined before use in performRegistration)
    const handleRegistrationSuccess = useCallback(() => {
        posthog?.capture(AnalyticsEvents.ONBOARDING_COMPLETED, {
            account_type: registrationData.accountType,
            public_profile: !!registrationData.instagramUsername,
            registration_method: registrationData.authMethod || 'email',
        })

        resetRegistrationData()

        // Check for marker to handle cleanup
        const markerStr = sessionStorage.getItem('authFlowMarker')
        if (markerStr) {
            try {
                const marker = JSON.parse(markerStr) as {
                    historyLength: number
                    startPath: string
                }
                const stepsBack = window.history.length - marker.historyLength
                if (stepsBack > 0) {
                    window.history.go(-stepsBack)
                    return
                }
            } catch (e) {
                console.error('Marker cleanup failed:', e)
                sessionStorage.removeItem('authFlowMarker')
            }
        }
        navigate({ to: returnToPath || '/' })
    }, [
        posthog,
        registrationData.accountType,
        registrationData.instagramUsername,
        registrationData.authMethod,
        resetRegistrationData,
        navigate,
        returnToPath,
    ])

    // Handle final registration
    const performRegistration = useCallback(
        async (type: AccountType) => {
            setApiError(null)
            setRegistrationData({ accountType: type })
            markStepCompleted('account-type')

            const role = type === 'creator' ? Role.STYLIST : Role.USER

            if (isSocialSignIn) {
                if (!registrationData.username) {
                    setApiError(
                        'Your username is missing. Please go back and enter a username.'
                    )
                    return
                }
                if (!registrationData.registrationToken) {
                    setApiError(
                        'Your authentication session is missing. Please restart the registration.'
                    )
                    return
                }

                // Refresh token before completing registration to prevent expiration errors
                const freshToken = await getIdToken(true)
                if (!freshToken) {
                    setApiError(
                        'Authentication session expired. Please sign in again.'
                    )
                    return
                }

                const registrationPayload = {
                    idToken: freshToken,
                    role: role,
                    username: registrationData.username,
                    stylePreference: registrationData.preference!,
                }

                const onSuccess = handleRegistrationSuccess
                const onError = (err: Error) => {
                    captureException(err, {
                        flow: 'registration',
                        step: 'account-type',
                        authMethod: registrationData.authMethod,
                    })
                    setApiError(getErrorMessage(err))
                }

                if (registrationData.authMethod === 'google') {
                    googleCompleteRegistration(registrationPayload, { onSuccess, onError })
                } else if (registrationData.authMethod === 'apple') {
                    appleCompleteRegistration(registrationPayload, { onSuccess, onError })
                }
            } else {
                const missingFields: string[] = []
                if (!registrationData.email) missingFields.push('email')
                if (!registrationData.password) missingFields.push('password')
                if (!registrationData.fullName) missingFields.push('full name')
                if (!registrationData.username) missingFields.push('username')

                if (missingFields.length > 0) {
                    setApiError(
                        `Your ${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} missing. Please restart the registration.`
                    )
                    return
                }
                const userData = {
                    email: registrationData.email!,
                    password: registrationData.password!,
                    username: registrationData.username!,
                    name: registrationData.fullName!,
                    stylePreference: registrationData.preference!,
                }
                registerUser(userData, {
                    onSuccess: () => {
                        // Auto login after successful registration, then send verification
                        loginUser(
                            {
                                email: registrationData.email!,
                                password: registrationData.password!,
                            },
                            {
                                onSuccess: () => {
                                    sendVerification({
                                        continueUrl: returnToPath || '/',
                                    })
                                    handleRegistrationSuccess()
                                },
                                onError: () =>
                                    setApiError(
                                        'Registration succeeded but auto-login failed. Please log in manually.'
                                    ),
                            }
                        )
                    },
                    onError: (err) => {
                        captureException(err, {
                            flow: 'registration',
                            step: 'account-type',
                            authMethod: 'email',
                        })
                        setApiError(getErrorMessage(err))
                    },
                })
            }
        },
        [
            registrationData,
            isSocialSignIn,
            setRegistrationData,
            markStepCompleted,
            googleCompleteRegistration,
            appleCompleteRegistration,
            registerUser,
            handleRegistrationSuccess,
            loginUser,
            sendVerification,
            returnToPath,
        ]
    )

    const handleBack = useCallback(() => {
        navigate({ to: '/register/preferences', search: { returnToPath, error: undefined } })
    }, [navigate, returnToPath])

    // Custom handler for AccountTypeCard selection
    const handleSelectAccountType = useCallback(
        (type: AccountType) => {
            setRegistrationData({ accountType: type })
            markStepCompleted('account-type')
            setOption(type)
        },
        [setRegistrationData, markStepCompleted]
    )

    const handleContinue = useCallback(async () => {
        if (option === 'collector') {
            await performRegistration(option)
        } else {
            navigate({ to: '/register/instagram', search: { returnToPath, error: undefined } })
        }
    }, [option, performRegistration, navigate, returnToPath])

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-2 pb-6 md:pt-[25dvh] md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        What type of account
                        <br className="" />
                        {/* KEEP BREAK ON DESKTOP ONLY ON ACCOUNT_TYPE ONBOARDING SCREEN */}
                        <span className="text-white-48">
                            {' '}
                            would you like to create?
                        </span>
                    </h1>
                </div>
            </FadeInUp>

            <div className="flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <CreatorCollectorCard
                        selectedType={option}
                        onAccountTypeSelected={handleSelectAccountType}
                    />
                    {isLoading && (
                        <p className="mt-4 animate-pulse text-center text-sm text-white">
                            Registering...
                        </p>
                    )}
                    {apiError && (
                        <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-red-500/20 p-2 backdrop-blur">
                            <AlertCircle
                                className="shrink-0 text-red-500"
                                size={16}
                            />
                            <p className="text-center text-xs text-red-400">
                                {apiError}
                            </p>
                        </div>
                    )}
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="flex items-center gap-2 pt-6">
                        <GlossyButton
                            variant="ghost"
                            className="rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                            onClick={handleBack}
                            disabled={isLoading}
                        >
                            <ArrowLeft
                                strokeWidth={1}
                                className="mr-1 h-5 w-5"
                            />
                            Back
                        </GlossyButton>
                        <GlossyButton
                            className="w-full rounded-xl py-[14px] text-sm"
                            onClick={handleContinue}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Checking...' : 'Continue'}
                        </GlossyButton>
                    </div>
                    <div className="mt-4 px-4 text-center">
                        <p className="text-sm font-normal text-white">
                            By creating an account, you agree that you have read
                            and accepted our{' '}
                            <Link
                                to="/terms-of-use"
                                className="font-semibold underline hover:text-white/80"
                            >
                                Terms of use.
                            </Link>
                        </p>
                    </div>
                </FadeInUp>
            </div>
        </section>
    )
}
